<template>
  <div class="section">
    <div class="container">
      <!-- Page Header -->
      <div class="mb-md">
        <div class="columns is-vcentered">
          <div class="column is-one-third">
            <breadcrumbs :history="[{ label: 'Settings', name: 'settings'}]" current="Shipping Accounts" />
          </div>
          <div class="column is-one-third">
            <h1 class="has-text-centered is-size-4">Shipping Accounts</h1>
          </div>
        </div>
      </div>
      <!-- Filters Toolbar -->
      <div class="has-background-white rounded-lg pa-md mb-xxs">
        <div class="columns">
          <div class="column">
            <b-select v-model="orderBy" size="is-medium" expanded @input="sort">
              <option value="-id">Most Recent</option>
              <option value="id">Oldest</option>
            </b-select>
          </div>
          <div class="column is-narrow">
            <b-button size="is-medium" @click="showCreateModal = true">Add Shipping Account</b-button>
          </div>
        </div>
      </div>
      <!-- Results -->
      <div>
        <div class="mb-md">
          <b-table
            :data="results"
            :mobile-cards="false"
            :loading="loading"
            hoverable
            class="is-size-7">
            <template>
              <!-- RC ID -->
              <b-table-column label="ID" header-class="nowrap" cell-class="nowrap" width="64" v-slot="props">
                {{ props.row.id }}
              </b-table-column>
              <!-- Nickname -->
              <b-table-column label="Nickname" header-class="nowrap" cell-class="nowrap" width="200" v-slot="props">
                <a href="#" class="has-text-weight-bold has-text-info" @click.prevent="edit(props.row)">
                  {{ props.row.nickname }}
                </a>
              </b-table-column>
              <!-- Carrier -->
              <b-table-column label="Carrier" header-class="nowrap" cell-class="nowrap" width="128" v-slot="props">
                {{ props.row.carrier.name }}
              </b-table-column>
              <!-- Account # -->
              <b-table-column label="Account #" header-class="nowrap" cell-class="nowrap" v-slot="props">
                {{ props.row.bill_third_party_account }}
              </b-table-column>
              <!-- Shipping Methods -->
              <b-table-column label="# Methods" header-class="nowrap" cell-class="nowrap" width="100" centered v-slot="props">
                {{ props.row.shipping_methods.length }}
              </b-table-column>
              <b-table-column label="" cell-class="nowrap" width="140" v-slot="props">
                <b-button
                  v-if="props.row.carrier.name === 'UPS' && props.row.identifier"
                  size="is-small"
                  :loading="checkingStatus === props.row.id"
                  @click="checkStatus(props.row)">
                  Check Status
                </b-button>
              </b-table-column>
            </template>
            <template #empty>
              <div class="py-xxl has-text-grey has-text-centered">
                {{ loading ? 'Loading data...' : 'No shipping accounts found!' }}
              </div>
            </template>
          </b-table>
        </div>
        <b-pagination
          :total="total"
          :current.sync="currentPage"
          :rounded="true"
          :per-page="perPage"
          @change="paginate">
        </b-pagination>
      </div>

      <!-- Create Carrier Account Modal -->
      <b-modal
        :active.sync="showCreateModal"
        :can-cancel="['x']"
        has-modal-card
        scroll="keep">
        <CreateShippingAccountModal @created="onCreated" />
      </b-modal>

      <!-- Edit Shipping Account Modal -->
      <b-modal
        :active.sync="showEditModal"
        :can-cancel="['escape', 'x']"
        has-modal-card
        scroll="keep"
        @close="account = null">
        <EditShippingAccountModal :account="account" @updated="load" />
      </b-modal>

      <!-- Carrier Status Modal -->
      <b-modal
        :active.sync="showStatusModal"
        :can-cancel="['escape', 'x']"
        has-modal-card
        scroll="keep"
        @close="statusData = null; statusCode = null">
        <div class="modal-card">
          <header class="modal-card-head">
            <p class="modal-card-title">Carrier Account Status</p>
          </header>
          <section class="modal-card-body" v-if="statusData">
            <!-- 200 OK — verified -->
            <b-message v-if="statusCode === 200" type="is-success" class="is-size-7">
              This carrier account has been successfully verified on EasyPost.
            </b-message>

            <!-- 201 Created — pending OAuth authorization -->
            <b-message v-else type="is-warning" class="is-size-7">
              <p class="has-text-weight-bold mb-xs">Authorization Required</p>
              <p class="mb-sm">This carrier account has not yet been authorized. Click the link below to complete authorization via the EasyPost partner portal.</p>
              <a :href="statusData.credentials.partner_oauth_url" target="_blank" rel="noopener noreferrer" class="has-text-weight-bold">
                Authorize Account &rarr;
              </a>
            </b-message>
          </section>
          <footer class="modal-card-foot">
            <b-button @click="showStatusModal = false">Close</b-button>
          </footer>
        </div>
      </b-modal>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import CreateShippingAccountModal from '../components/ShippingAccounts/CreateShippingAccountModal'
import EditShippingAccountModal from '../components/ShippingAccounts/EditShippingAccountModal'
import Filterable from '@/mixins/Filterable'

export default {
  components: {
    CreateShippingAccountModal,
    EditShippingAccountModal
  },
  data () {
    return {
      key: 'shipping-accounts',
      loading: true,
      account: null,
      showCreateModal: false,
      showEditModal: false,
      showStatusModal: false,
      statusData: null,
      statusCode: null,
      checkingStatus: null
    }
  },

  computed: {
    ...mapGetters(['context']),
    endpoint () {
      return this.context.route + '/shippingaccounts/'
    }
  },
  methods: {
    edit (account) {
      this.account = account
      this.showEditModal = true
    },
    onCreated () {
      this.showCreateModal = false
      this.load()
    },
    checkStatus (account) {
      this.checkingStatus = account.id
      const endpoint = this.context.route + `/shippingaccounts/carrier-account/?carrier_account_id=${account.identifier}`
      this.$http.get(endpoint).then(response => {
        this.statusData = response.data
        this.statusCode = response.status
        this.showStatusModal = true
      }).catch(() => {
        this.$buefy.toast.open({ type: 'is-danger', message: 'Failed to fetch carrier account status.' })
      }).finally(() => {
        this.checkingStatus = null
      })
    }
  },
  mixins: [Filterable],
  mounted () {
    this.load()
  }
}
</script>
